import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Person,
  Security,
  Devices,
  DeleteOutline,
  Refresh,
  Shield,
  PhoneAndroid,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUser } from '../features/auth/authSlice';
import { api } from '../services/api';

interface MFAStatus {
  mfa_enabled: boolean;
  mfa_verified_at: string | null;
  backup_codes_remaining: number;
}

interface Session {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  last_activity: string;
  expires_at: string;
  is_current: boolean;
}

interface TrustedDevice {
  id: string;
  device_name: string | null;
  device_fingerprint: string;
  ip_address: string | null;
  created_at: string;
  last_used_at: string;
  expires_at: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load MFA status
      try {
        const mfaData = await api.get<MFAStatus>('/auth/mfa/status');
        if (mfaData) {
          setMfaStatus(mfaData);
        }
      } catch (err) {
        console.log('MFA status not available', err);
        setMfaStatus(null);
      }

      // Load sessions
      try {
        const sessionsData = await api.get<Session[]>('/auth/sessions');
        // Ensure it's an array
        if (Array.isArray(sessionsData)) {
          setSessions(sessionsData);
        } else {
          setSessions([]);
        }
      } catch (err) {
        console.log('Sessions not available');
        setSessions([]);
      }

      // Load trusted devices if MFA is enabled
      if (mfaStatus?.mfa_enabled) {
        try {
          const devicesData = await api.get<{ devices: TrustedDevice[] }>('/auth/mfa/trusted-devices');
          if (devicesData && Array.isArray(devicesData.devices)) {
            setTrustedDevices(devicesData.devices);
          } else {
            setTrustedDevices([]);
          }
        } catch (err) {
          console.log('Trusted devices not available', err);
          setTrustedDevices([]);
        }
      }
    } catch (err: any) {
      setError(err.message || t('profile.failedToLoadProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      setSuccessMessage(t('profile.sessionDeleted'));
      loadProfileData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || t('profile.failedToDelete'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleRemoveTrustedDevice = async (deviceId: string) => {
    try {
      await api.delete(`/auth/mfa/trusted-devices/${deviceId}`);
      setSuccessMessage(t('profile.deviceRemoved'));
      loadProfileData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || t('profile.failedToRemove'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getUserAgent = (userAgent: string | null) => {
    if (!userAgent) return 'Unknown';
    // Simple parsing for display
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="error">{t('common.userInfoNotAvailable')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('profile.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* User Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Person color="primary" />
                <Typography variant="h6">{t('profile.userInfo')}</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ '& > *': { mb: 2 } }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.name')}
                  </Typography>
                  <Typography variant="body1">{user.fullName || user.display_name || 'N/A'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.email')}
                  </Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.role')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={user.role} size="small" color="primary" />
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.tenant')}
                  </Typography>
                  <Typography variant="body1">{user.tenantCode}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.groups')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {user.groups && user.groups.length > 0 ? (
                      user.groups.map((group) => (
                        <Chip key={group} label={group} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('common.noGroups')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* MFA Status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Security color="primary" />
                <Typography variant="h6">{t('profile.security')}</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {mfaStatus ? (
                <Box sx={{ '& > *': { mb: 2 } }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.mfa')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        icon={<Shield />}
                        label={mfaStatus.mfa_enabled ? t('profile.mfaEnabled') : t('profile.mfaDisabled')}
                        color={mfaStatus.mfa_enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {mfaStatus.mfa_enabled && (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('profile.lastVerified')}
                        </Typography>
                        <Typography variant="body2">
                          {mfaStatus.mfa_verified_at ? formatDate(mfaStatus.mfa_verified_at) : 'N/A'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('profile.backupCodesRemaining')}
                        </Typography>
                        <Typography variant="body2">
                          {mfaStatus.backup_codes_remaining} / 10
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled
                      fullWidth
                    >
                      {mfaStatus.mfa_enabled ? t('profile.manageMfa') : t('profile.enableMfa')}
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      ※ {t('common.comingSoon')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">{t('profile.mfaNotAvailable')}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Sessions */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Devices color="primary" />
                <Typography variant="h6">{t('profile.activeSessions')}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton size="small" onClick={loadProfileData}>
                  <Refresh />
                </IconButton>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {sessions.length > 0 ? (
                <List>
                  {sessions.map((session) => (
                    <ListItem key={session.id} divider>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">
                              {getUserAgent(session.user_agent)}
                            </Typography>
                            {session.is_current && (
                              <Chip label={t('common.currentSession')} size="small" color="primary" />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              IP: {session.ip_address || 'N/A'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {t('common.lastActivity')}: {formatDate(session.last_activity)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {t('common.expires')}: {formatDate(session.expires_at)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        {!session.is_current && (
                          <IconButton
                            edge="end"
                            aria-label={t('profile.deleteSession')}
                            onClick={() => handleDeleteSession(session.id)}
                            size="small"
                          >
                            <DeleteOutline />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">{t('profile.noActiveSessions')}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Trusted Devices (only if MFA is enabled) */}
        {mfaStatus?.mfa_enabled && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <PhoneAndroid color="primary" />
                  <Typography variant="h6">{t('profile.trustedDevices')}</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                {trustedDevices.length > 0 ? (
                  <List>
                    {trustedDevices.map((device) => (
                      <ListItem key={device.id} divider>
                        <ListItemText
                          primary={device.device_name || t('profile.unnamedDevice')}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                IP: {device.ip_address || 'N/A'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {t('common.registeredAt')}: {formatDate(device.created_at)}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {t('common.lastUsed')}: {formatDate(device.last_used_at)}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {t('common.expires')}: {formatDate(device.expires_at)}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label={t('profile.removeTrustedDevice')}
                            onClick={() => handleRemoveTrustedDevice(device.id)}
                            size="small"
                          >
                            <DeleteOutline />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">{t('profile.noTrustedDevices')}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ProfilePage;
